import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListCasesResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { ViewBar } from '../shared/view-bar.js';
import { useSavedView, visibleColumns } from '../shared/use-saved-view.js';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-58 Cases, as a list.
 *
 * Beside the queue, not instead of it. The queue is what is still to be
 * worked: breached first, then due soonest, closed cases left out. That is the
 * right primary view and it cannot answer "what did we close last month", "how
 * many did Omar resolve", or "everything from that customer" - not because it
 * is wrong, but because those are a different question, and a queue that tried
 * to answer them would stop being a queue.
 *
 * The SLA state is a column here too. A list of closed cases with no
 * indication of which breached is a list of subjects, and which breached is
 * the thing a support manager opened this to find.
 */

type Row = ListCasesResult['rows'][number];

function CasesRecordListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'created',
    direction: 'desc',
  });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  // A cursor is a position in one order, so changing the view or the grouping
  // makes the page it names a page of something else.
  const savedView = useSavedView(() => setCursorStack([]));

  const query = useQuery<ListCasesResult, ApiError>({
    queryKey: ['cases-list', { search, sort, cursor, view: savedView.params }],
    queryFn: () =>
      api.listCases({
        query: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
          ...savedView.params,
          sort: sort.field,
          direction: sort.direction,
          limit: 50,
        },
      }),
  });

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'number',
        header: t('list.column.sku'),
        sortField: 'number',
        cell: (row) => row.number,
      },
      {
        key: 'subject',
        header: t('list.column.subject'),
        sortField: 'subject',
        essential: true,
        cell: (row) => row.subject,
      },
      {
        key: 'status',
        header: t('list.column.status'),
        sortField: 'status',
        essential: true,
        cell: (row) => row.status,
      },
      {
        key: 'priority',
        header: t('list.column.priority'),
        sortField: 'priority',
        cell: (row) => row.priority,
        hideOnCard: true,
        advancedOnly: true,
      },
      {
        key: 'sla',
        header: 'SLA',
        /*
         * Kept in Simple mode.
         *
         * Which cases breached is the question a support manager opens this
         * list to answer; a list of subjects with the clock budgeted away is
         * the queue with fewer columns. `priority` is the one that goes.
         */
        essential: true,
        cell: (row) => (
          /*
           * The urgency the engine decided, not a second reading of two
           * timestamps. One value decides whether this is a breach, and the
           * queue column, the record header and this row all read it.
           */
          <span
            data-case-sla={row.caseId}
            className={row.sla.urgency === 'breached' ? 'text-danger-text' : 'text-ink-meta'}
          >
            {row.sla.urgency}
          </span>
        ),
      },
      {
        key: 'created',
        header: t('list.column.updated'),
        sortField: 'created',
        cell: (row) => <Num className="text-ink-meta">{formatRelativeDate(row.createdAt, format)}</Num>,
        hideOnCard: true,
      },
    ],
    [t, format],
  );

  const rows = query.data?.rows ?? [];
  const visible = useMemo(
    () => visibleColumns(columns, query.data?.columns ?? null),
    [columns, query.data?.columns],
  );

  /*
   * What a group heading says, per groupable field.
   *
   * Written out rather than indexed off the row: the API field name and the
   * row property are deliberately different - `company` is `accountName` -
   * and a lookup by string would be another place the two vocabularies have
   * to agree.
   */
  const groupLabel: Record<string, (row: Row) => string | null> = {
    status: (row) => t(`case.status.${row.status}` as 'case.status.new'),
    priority: (row) => t(`case.priority.${row.priority}` as 'case.priority.normal'),
    assignee: (row) => row.assignedToName,
    company: (row) => row.accountName,
  };
  const groupBy = query.data?.groupBy ?? null;
  const grouping = groupBy ? groupLabel[groupBy] : undefined;

  // The groups the server says it will honour, labelled here: it decides what
  // works, the screen decides what it is called.
  const groupNames: Record<string, string> = {
    status: t('case.status'),
    priority: t('case.priority'),
    assignee: t('case.assignee'),
    company: t('object.account'),
  };
  const groupable = (query.data?.groupable ?? [])
    .filter((field) => Object.hasOwn(groupNames, field))
    .map((field) => ({ field, label: groupNames[field]! }));

  return (
    <RecordList<Row>
      title={t('list.cases.title')}
      icon="Lifebuoy"
      rowId={(row) => row.caseId}
      recordHref={(row) => `/cases/${row.caseId}`}
      columns={visible}
      {...(grouping ? { group: { label: grouping, emptyLabel: t('view.group.empty') } } : {})}
      viewBar={
        <ViewBar
          objectKey="case"
          active={savedView.active}
          draft={savedView.draft}
          columns={columns.map((column) => ({ key: column.key, label: column.header }))}
          groupable={groupable}
          onChange={savedView.onChange}
          onSelect={savedView.onSelect}
          onRemoveFilter={savedView.onRemoveFilter}
          onDiscard={savedView.onDiscard}
        />
      }
      page={
        query.data
          ? {
              rows,
              total: query.data.total,
              hasMore: query.data.hasMore,
              nextCursor: query.data.nextCursor,
            }
          : undefined
      }
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={(value) => {
        setSearch(value);
        setCursorStack([]);
      }}
      sort={sort}
      onSortChange={(field) => {
        setSort((current) => nextSort(current, field));
        setCursorStack([]);
      }}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={query.data?.limit ?? 50}
      onClearFilters={() => {
        setSearch('');
        setCursorStack([]);
      }}
      filtered={search.trim().length > 0}
      aboveTable={
        <div className="flex justify-end">
          <Link data-cases-queue to="/cases" className="text-dense text-bronze-text hover:text-ink">
            {t('list.cases.queue')}
          </Link>
        </div>
      }
      emptyTitle={t('list.cases.empty.title')}
      emptyBody={t('list.cases.empty.body')}
      emptyIllustration="Lifebuoy"
    />
  );
}

function CasesRecordListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Lifebuoy"
      title={t('list.cases.empty.title')}
      body={t('list.cases.empty.body')}
      /*
       * The queue, not a "New case" button. A case is raised from the queue,
       * from an inbound email, from the portal or from a chat - the list is
       * where they are read back, and the queue is what can make one.
       */
      secondary={{ label: t('list.cases.queue'), href: '/cases' }}
    />
  );
}

function CasesRecordListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading cases</span>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function CasesRecordListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Cases could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-58',
  route: '/cases/list',
  surface: 'page',
  title: 'Cases list',
  features: ['F-085'],
  entitlement: 'F-085',
  reachedBy: [{ kind: 'screen', from: 'SC-28', how: 'the "Show as a list" link above the queue' }],
  components: ['DataTable', 'FilterChips', 'ViewBar'],
  elements: ['RecordList'],
  states: {
    empty: CasesRecordListEmpty,
    loading: CasesRecordListLoading,
    error: CasesRecordListError,
  },
  component: CasesRecordListScreen,
});

export default screen;
